// Best-effort client-side redaction of personal information from the text that
// will be written into an exported .wat file. Conservative patterns ("where
// practical") — the user always sees a preview of exactly what will be saved.

const RULES = [
  { name: "email", re: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, label: "[redacted-email]" },
  { name: "ssn", re: /\b\d{3}-\d{2}-\d{4}\b/g, label: "[redacted-ssn]" },
  {
    name: "card",
    re: /\b(?:\d[ -]?){13,19}\b/g,
    label: "[redacted-card]",
    when: (m) => (m.replace(/[^\d]/g, "").length >= 13),
  },
  { name: "ip", re: /\b(?:\d{1,3}\.){3}\d{1,3}\b/g, label: "[redacted-ip]" },
  {
    name: "phone",
    re: /(?:\+?\d[\d\s().-]{8,}\d)/g,
    label: "[redacted-phone]",
    when: (m) => (m.replace(/[^\d]/g, "").length >= 10 && m.replace(/[^\d]/g, "").length <= 15),
  },
];

// Returns { text, counts } — counts is { email: n, phone: n, ... } (only >0).
export function redactText(input) {
  const counts = {};
  let text = typeof input === "string" ? input : "";
  if (!text) return { text, counts };
  for (const rule of RULES) {
    text = text.replace(rule.re, (m) => {
      if (rule.when && !rule.when(m)) return m;
      counts[rule.name] = (counts[rule.name] || 0) + 1;
      return rule.label;
    });
  }
  return { text, counts };
}

function mergeCounts(into, add) {
  for (const k of Object.keys(add || {})) into[k] = (into[k] || 0) + add[k];
}

// Redact every user-facing text field of a portable payload IN PLACE-ish
// (returns a new payload) and report aggregate counts for the preview.
export function redactPayload(payload) {
  const counts = {};
  const rt = (s) => {
    const r = redactText(s);
    mergeCounts(counts, r.counts);
    return r.text;
  };
  const turns = (payload.turns || []).map((t) => {
    const env = t.envelope || {};
    return {
      ...t,
      user_input: rt(t.user_input || ""),
      envelope: {
        ...env,
        answer: rt(env.answer || ""),
        depth_topic: rt(env.depth_topic || ""),
        proactions: (env.proactions || []).map((p) => ({ ...p, text: rt(p.text || "") })),
        belief_change: env.belief_change
          ? { ...env.belief_change, text: rt(env.belief_change.text || "") }
          : null,
        continuations: (env.continuations || []).map((c) => rt(c)),
        deeper_alternatives: (env.deeper_alternatives || []).map((c) => rt(c)),
        structured_blocks: (env.structured_blocks || []).map((b) => ({
          ...b,
          title: rt(b.title || ""),
          rows: (b.rows || []).map((row) => row.map((cell) => rt(String(cell)))),
        })),
      },
    };
  });
  return { payload: { ...payload, turns }, counts };
}

export const redactionSummary = (counts) => {
  const labels = { email: "email", phone: "phone number", card: "card number", ssn: "SSN", ip: "IP address" };
  const parts = Object.keys(counts)
    .filter((k) => counts[k] > 0)
    .map((k) => `${counts[k]} ${labels[k] || k}${counts[k] > 1 ? "s" : ""}`);
  return parts.length ? `Redacted ${parts.join(", ")}.` : "No personal information detected to redact.";
};
